import {
  Body,
  Box,
  BoxProps,
  Heading,
  LinkProps,
  Stack,
  TextInput,
  VStack,
} from '@hover/blueprint';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Success from './Success';
import GetStarted from './GetStarted';
import {
  countrycodeWrapperStyle,
  phoneNumErrorStyle,
  wrapperStyle,
} from './styles';

const VALIDATION = /^(()?\d{3}())?(-|\s)?\d{3}(-|\s)?\d{4}$/;

declare global {
  interface Window {
    analytics: { track: any };
  }
}

interface ISignUpCTAFormProps extends BoxProps {
  inputBackground?: string;
  contentColor?: string;
  inputColor?: string;
  description?: string;
  linkColorScheme?: LinkProps['colorScheme'];
  shouldTrack?: boolean;
}

const SignUpCTAForm: React.FC<ISignUpCTAFormProps> = props => {
  const { inputBackground, inputColor, shouldTrack } = props;
  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  function getDashedValue(): string {
    const { phoneNumber } = watch();
    if (phoneNumber && typeof phoneNumber === 'string') {
      const numWithoutDashes = phoneNumber.replace(/[^0-9]/g, '');
      const numLength = numWithoutDashes.length;
      if (numLength) {
        if (numLength > 0 && numLength < 4) {
          return `${numWithoutDashes.slice(0, 3)}`;
        }
        if (numLength > 3 && numLength < 7) {
          return `${numWithoutDashes.slice(0, 3)}-${numWithoutDashes.slice(
            3,
            6,
          )}`;
        }
        if (numLength > 6) {
          return `${numWithoutDashes.slice(0, 3)}-${numWithoutDashes.slice(
            3,
            6,
          )}-${numWithoutDashes.slice(6)}`;
        }
      }
      return '';
    }
    return '';
  }

  function onSubmit({ phoneNumber }: { phoneNumber: string }) {
    setLoading(true);
    if (phoneNumber.length) {
      setLoading(true);
      const bodyParams = JSON.stringify({
        number: phoneNumber.replaceAll('-', ''),
      });

      fetch('https://hover.to/api/v1/users/send_homeowner_sms', {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        body: bodyParams,
      })
        .then(res => {
          setLoading(false);
          if (res.status === 200) {
            setShowSuccessMessage(true);
            if (window.analytics && shouldTrack) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              window.analytics.track('Download app SMS form submitted', {
                phoneNumber,
              });
            }
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }

  return (
    <Stack
      {...wrapperStyle}
      direction={{ base: 'column', tablet: 'row' }}
      spacing={{ base: errors?.phoneNumber ? '30px' : '78px', tablet: 400 }}
    >
      {!showSuccessMessage ? (
        <>
          <Box alignItems="flex-start">
            <Box {...countrycodeWrapperStyle} bg={inputBackground}>
              <>🇺🇸</>
              <Heading ml={200} size={200} marginBottom={0} as="p">
                +1
              </Heading>
            </Box>
            <VStack spacing="10px" w={{ base: '161px', tablet: '134px' }}>
              <TextInput
                bg={inputBackground}
                {...register('phoneNumber', {
                  required: 'error message',
                  pattern: VALIDATION,
                })}
                value={getDashedValue()}
                borderColor="neutral.0"
                color={inputColor}
                fontWeight={400}
                borderWidth="1px"
                placeholder="818-123-4567"
                _placeholder={{
                  color: 'brandNavy.300',
                }}
                borderStyle="solid"
              />
              {errors?.phoneNumber && (
                <Body {...phoneNumErrorStyle} size={300}>
                  Please enter a U.S. phone number in the correct format. All
                  numbers, no dashes.
                </Body>
              )}
            </VStack>
          </Box>
          <GetStarted
            {...props}
            onSubmit={handleSubmit(onSubmit)}
            isLoading={loading}
          />
        </>
      ) : (
        <Success />
      )}
    </Stack>
  );
};

SignUpCTAForm.defaultProps = {
  inputBackground: 'neutral.0',
  contentColor: 'neutral.0',
  inputColor: 'brandNavy.300',
  description: '',
  linkColorScheme: 'neutral',
  shouldTrack: false,
};

export default SignUpCTAForm;
